/**
 * `/trainers/:id` — a trainer's dedicated page.
 *
 * Reached by clicking a trainer's id or name in the `/data/trainers` browser.
 * The trainer-side counterpart to `MemberDetail`. Renders one snapshot: contact
 * details, headline performance metrics (sessions completed, trial→customer
 * conversion, rebooking/retention, membership upsell, active clients), the
 * trainer's sessions (past + upcoming), the members they've trained (with a
 * derived lifecycle), their recurring availability + upcoming slots, the
 * session notes they authored, and their trainer-memory markdown. All sourced
 * from `GET /api/trainers/:id` (one aggregate fetch).
 *
 * Read-only but for ONE thing (2026-08-06): each session note carries ✎ / ×,
 * dispatching `edit_session_note` / `delete_session_note` and refetching. See
 * {@link TrainerSessionNotes} — a note filed against the wrong member is
 * correctable from either the member's page or its author's, and both go
 * through the same canonical actions and the same `lib/session-note-edit.ts`.
 *
 * The Satisfaction tile is a deliberate placeholder until Phase 2 ships the
 * session-feedback collection that backs a real score.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  api,
  ApiError,
  type TrainerDetailMemberTrained,
  type TrainerDetailResponse,
  type TrainerDetailSession,
  type TrainerDetailSessionNote,
} from "../lib/api";
import { dispatchAction } from "../lib/dispatch";
import { CollapsibleJson } from "../components/json-tree";
import { SessionNoteEditor } from "../components/SessionNoteEditor";
import { ProfileEditRow } from "../components/ProfileEditRow";
import { useDispatch } from "../lib/use-dispatch";
import { studioFormat } from "../lib/studio-tz";
import { sessionNoteDeleteConfirmMessage } from "../lib/session-note-edit";

const DATETIME_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
};

const DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "2-digit",
};

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATETIME_OPTS).format(d);
}

function fmtDate(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : studioFormat(DATE_OPTS).format(d);
}

/** `09:00:00` → `09:00`; tolerant of already-short or null values. */
function fmtTime(t: string | null): string {
  if (t === null || t.length === 0) return "—";
  return t.length >= 5 ? t.slice(0, 5) : t;
}

function fmtWeekday(weekday: number): string {
  return WEEKDAYS[weekday] ?? `day ${weekday}`;
}

/** A ratio (0..1) or null → a rounded percentage string. */
function fmtPct(ratio: number | null): string {
  return ratio === null ? "—" : `${Math.round(ratio * 100)}%`;
}

/** Map a status-ish string to a tag tone (green = good, red = bad). */
function statusTone(status: string): string {
  const s = status.toLowerCase();
  if (["active", "completed", "attended", "customer"].includes(s)) {
    return "tag-success";
  }
  if (
    [
      "cancelled",
      "canceled",
      "failed",
      "no_show",
      "past_due",
      "deleted",
      "suspended",
    ].includes(s)
  ) {
    return "tag-error";
  }
  return "";
}

function Tag({ value }: { readonly value: string }): JSX.Element {
  return <span className={`tag ${statusTone(value)}`}>{value}</span>;
}

export function TrainerDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TrainerDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  // "Copy pay link" — mints a 30-day signed link to this trainer's own
  // month-to-date figure and puts it on the clipboard for Mario to send
  // HIMSELF. The trainerops agent never holds or sends it: T6 admits a
  // self-serve provisional page, not an agent that quotes pay.
  const [payLink, setPayLink] = useState<
    { url: string; expiresAt: string } | null
  >(null);
  const [payLinkMsg, setPayLinkMsg] = useState<string | null>(null);
  const [payLinkBusy, setPayLinkBusy] = useState(false);

  useEffect(() => {
    if (id === undefined) {
      setError("missing_trainer_id");
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .trainer(id)
      .then((r) => {
        if (cancelled) return;
        setData(r);
        setError(null);
      })
      .catch((e: Error) => {
        if (cancelled) return;
        setError(
          e instanceof ApiError ? e.code ?? `http_${e.status}` : e.message,
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const copyPayLink = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    setPayLinkBusy(true);
    setPayLinkMsg(null);
    try {
      const r = await api.trainerPayLink(id);
      const url = `${window.location.origin}${r.path}`;
      setPayLink({ url, expiresAt: r.expires_at });
      try {
        await navigator.clipboard.writeText(url);
        setPayLinkMsg("Copied — paste it to them.");
      } catch {
        // Clipboard is permission-gated and unavailable over plain HTTP on
        // some browsers. The link is shown below regardless, so the button
        // still works; only the convenience is lost.
        setPayLinkMsg("Couldn't copy automatically — select the link below.");
      }
    } catch (e) {
      setPayLinkMsg(
        e instanceof ApiError && e.code === "trainer_pay_unconfigured"
          ? "Pay links aren't configured on this deployment (LAYER_TRAINER_PAY_HMAC_SECRET is unset)."
          : `Couldn't mint a link: ${e instanceof ApiError ? e.code ?? e.message : String(e)}`,
      );
    } finally {
      setPayLinkBusy(false);
    }
  }, [id]);

  /** Silent refetch after a note edit/delete — keeps the view on a transient
   *  error rather than blanking the page the operator is working in. */
  const refresh = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    try {
      setData(await api.trainer(id));
    } catch {
      // Deliberately swallowed; the previous snapshot stays on screen.
    }
  }, [id]);

  if (loading) {
    return <p className="empty">Loading trainer…</p>;
  }
  if (error !== null) {
    const notFound = error === "trainer_not_found";
    return (
      <div>
        <p className="member-breadcrumb">
          <Link to="/data/trainers" className="data-member-link">
            ← Trainers
          </Link>
        </p>
        <p className="empty">
          {notFound
            ? "Trainer not found in this studio."
            : `Couldn't load this trainer (${error}).`}
        </p>
      </div>
    );
  }
  if (data === null) {
    return <p className="empty">Trainer not found.</p>;
  }

  const t = data.trainer;
  const mx = data.metrics;
  const gcalId =
    typeof t.metadata["gcal_id"] === "string"
      ? (t.metadata["gcal_id"] as string)
      : null;

  return (
    <div>
      <p className="member-breadcrumb">
        <Link to="/data/trainers" className="data-member-link">
          ← Trainers
        </Link>
      </p>
      <h1 className="page-title member-title">
        {t.display_name} <Tag value={t.status} />
      </h1>
      <p className="member-breadcrumb">
        <Link
          to={`/trainers/${encodeURIComponent(t.id)}/today`}
          className="data-member-link"
        >
          Today&apos;s brief →
        </Link>
      </p>

      {/* The trainer's own month-to-date page. Minted here and sent by hand:
          the link is a credential scoped to this one trainer for 30 days, and
          the lane's standing rule is that the agent hands links to Mario, not
          to trainers. */}
      <div className="tpay-issue">
        <button
          type="button"
          className="pr-toggle"
          disabled={payLinkBusy}
          onClick={() => void copyPayLink()}
        >
          {payLinkBusy ? "Minting…" : "Copy pay link"}
        </button>
        {payLinkMsg !== null ? (
          <span className="tpay-issue-msg">{payLinkMsg}</span>
        ) : null}
        {payLink !== null ? (
          <div className="tpay-issue-url">
            <code>{payLink.url}</code>
            <span className="subtle">
              {" "}
              · valid until {new Date(payLink.expiresAt).toLocaleDateString(
                "en-GB",
                { day: "numeric", month: "long", year: "numeric" },
              )}
            </span>
          </div>
        ) : null}
      </div>

      {/* Headline performance figures. */}
      <div className="stat-grid">
        <StatCard
          label="Sessions completed"
          value={String(mx.sessions_completed)}
        />
        <StatCard
          label="Trial → customer"
          value={fmtPct(mx.trial_to_customer_rate)}
          hint={`${mx.trial_to_customer_numerator} of ${mx.trial_to_customer_denominator} trials originated by this trainer became customers (≥2 purchases).`}
        />
        <StatCard
          label="Rebooking rate"
          value={fmtPct(mx.rebooking_rate)}
          hint={`${mx.repeat_clients} of ${mx.distinct_members} members trained came back for a 2nd session.`}
        />
        <StatCard
          label="Membership upsell"
          value={fmtPct(mx.membership_upsell_rate)}
          hint="Share of members trained who hold an active recurring membership."
        />
        <StatCard
          label="Active clients (90d)"
          value={String(mx.active_clients_90d)}
          hint="Distinct members trained in the last 90 days."
        />
        <StatCard label="Next session" value={fmtDateTime(mx.next_session_at)} />
        <StatCard
          label="Satisfaction"
          value={
            mx.satisfaction_count === 0 || mx.satisfaction_avg === null
              ? "—"
              : `${mx.satisfaction_avg.toFixed(1)} ★`
          }
          hint={
            mx.satisfaction_count === 0
              ? "No member ratings yet."
              : `Average of ${mx.satisfaction_count} member rating(s), 1–3.`
          }
        />
      </div>

      {/* Contact / account */}
      <section className="panel">
        <h2>Account details</h2>
        <dl className="kvs">
          <dt>Trainer ID</dt>
          <dd>
            <code>{t.id}</code>
          </dd>
          <dt>Name</dt>
          <dd>{t.display_name}</dd>
          <dt>Email</dt>
          <TrainerEmailEditor
            trainerId={t.id}
            value={t.email}
            onSaved={() => void refresh()}
          />
          <dt>Phone</dt>
          <dd>{t.phone ?? "—"}</dd>
          <dt>Google Calendar</dt>
          <dd>{gcalId ?? "—"}</dd>
          <dt>Status</dt>
          <dd>{t.status}</dd>
          <dt>Added</dt>
          <dd>{fmtDateTime(t.created_at)}</dd>
          <dt>Last updated</dt>
          <dd>{fmtDateTime(t.updated_at)}</dd>
          <dt>Metadata</dt>
          <dd>
            {Object.keys(t.metadata).length === 0 ? (
              "—"
            ) : (
              <CollapsibleJson value={t.metadata} />
            )}
          </dd>
        </dl>
      </section>

      {/* Upcoming sessions */}
      <section className="panel">
        <h2>Upcoming sessions ({data.upcoming_sessions.length})</h2>
        <SessionsTable rows={data.upcoming_sessions} showAttendance={false} />
      </section>

      {/* Past sessions */}
      <section className="panel">
        <h2>Past sessions ({data.past_sessions.length})</h2>
        <SessionsTable rows={data.past_sessions} showAttendance />
      </section>

      {/* Members trained */}
      <section className="panel">
        <h2>Members trained ({data.members_trained.length})</h2>
        <p className="member-note">
          Lifecycle is derived: <strong>trial</strong> = booked but fewer than 2
          purchases, <strong>customer</strong> = 2 or more purchases. ★ marks a
          member whose first-ever session was with this trainer.
        </p>
        {data.members_trained.length === 0 ? (
          <p className="empty">No members trained yet.</p>
        ) : (
          <div className="table-scroll-x">
            <table>
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Lifecycle</th>
                  <th>Sessions</th>
                  <th>First session</th>
                  <th>Repeat</th>
                </tr>
              </thead>
              <tbody>
                {data.members_trained.map((mt) => (
                  <tr key={mt.member_id}>
                    <td>
                      <Link
                        to={`/members/${encodeURIComponent(mt.member_id)}`}
                        className="data-member-link"
                      >
                        {memberLabel(mt)}
                      </Link>
                    </td>
                    <td>
                      <Tag value={mt.lifecycle} />
                    </td>
                    <td>{mt.sessions_with_trainer}</td>
                    <td>{fmtDate(mt.first_session_at)}</td>
                    <td>{mt.is_repeat ? "yes" : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Availability */}
      <section className="panel">
        <h2>Recurring availability ({data.availability.length})</h2>
        {data.availability.length === 0 ? (
          <p className="empty">No recurring availability configured.</p>
        ) : (
          <div className="table-scroll-x">
            <table>
              <thead>
                <tr>
                  <th>Day</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Capacity</th>
                </tr>
              </thead>
              <tbody>
                {data.availability.map((w, i) => (
                  <tr key={`${w.weekday}:${w.start_time}:${i}`}>
                    <td>{fmtWeekday(w.weekday)}</td>
                    <td>{fmtTime(w.start_time)}</td>
                    <td>{fmtTime(w.end_time)}</td>
                    <td>{w.session_type ?? "—"}</td>
                    <td>{w.location ?? "—"}</td>
                    <td>{w.capacity}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Upcoming slots */}
      <section className="panel">
        <h2>Upcoming slots ({data.upcoming_slots.length})</h2>
        {data.upcoming_slots.length === 0 ? (
          <p className="empty">No upcoming bookable slots.</p>
        ) : (
          <div className="table-scroll-x">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Location</th>
                  <th>Taken</th>
                  <th>Capacity</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.upcoming_slots.map((s) => (
                  <tr key={s.id}>
                    <td>{fmtDateTime(s.starts_at)}</td>
                    <td>{s.session_type ?? "—"}</td>
                    <td>{s.location ?? "—"}</td>
                    <td>{s.taken_count}</td>
                    <td>{s.capacity}</td>
                    <td>
                      <Tag value={s.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Session notes */}
      <TrainerSessionNotes
        notes={data.session_notes}
        onChanged={() => void refresh()}
      />

      {/* Trainer memory */}
      <section className="panel">
        <h2>Trainer memory</h2>
        {data.memory === null ? (
          <p className="empty">No memory recorded for this trainer.</p>
        ) : (
          <div>
            <p className="member-note">
              {data.memory.rolling_day_count} day(s) in the rolling window · last
              entry {fmtDate(data.memory.last_entry_on)} · updated{" "}
              {fmtDateTime(data.memory.updated_at)}
            </p>
            <MemoryBlock label="Summary" body={data.memory.summary_md} />
            <MemoryBlock label="Claude notes" body={data.memory.claude_md} />
            <MemoryBlock label="Long-term" body={data.memory.longterm_md} />
            <MemoryBlock label="Rolling" body={data.memory.rolling_md} />
          </div>
        )}
      </section>

      <p className="member-note">
        Snapshot computed {fmtDateTime(data.computed_at)}.
      </p>
    </div>
  );
}

function memberLabel(mt: TrainerDetailMemberTrained): string {
  const name = mt.member_name ?? mt.member_id.slice(0, 8);
  return mt.first_with_trainer ? `★ ${name}` : name;
}

function StatCard({
  label,
  value,
  hint,
}: {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}): JSX.Element {
  return (
    <div className="stat-card" title={hint}>
      <div className="label">{label}</div>
      <div className="value member-stat-value">{value}</div>
    </div>
  );
}

function MemoryBlock({
  label,
  body,
}: {
  readonly label: string;
  readonly body: string;
}): JSX.Element | null {
  if (body.trim().length === 0) return null;
  return (
    <details className="member-note">
      <summary>{label}</summary>
      <pre className="memory-md">{body}</pre>
    </details>
  );
}

function SessionsTable({
  rows,
  showAttendance,
}: {
  readonly rows: ReadonlyArray<TrainerDetailSession>;
  readonly showAttendance: boolean;
}): JSX.Element {
  if (rows.length === 0) {
    return <p className="empty">None.</p>;
  }
  return (
    <div className="table-scroll-x">
      <table>
        <thead>
          <tr>
            <th>When</th>
            <th>Member</th>
            <th>Type</th>
            <th>Location</th>
            <th>Status</th>
            {showAttendance ? <th>Attendance</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td>{fmtDateTime(s.starts_at)}</td>
              <td>
                <Link
                  to={`/members/${encodeURIComponent(s.member_id)}`}
                  className="data-member-link"
                >
                  {s.member_name ?? s.member_id.slice(0, 8)}
                </Link>
              </td>
              <td>{s.session_type ?? "—"}</td>
              <td>{s.location ?? "—"}</td>
              <td>
                <Tag value={s.status} />
              </td>
              {showAttendance ? (
                <td>
                  <Tag value={s.attendance_status} />
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The notes this trainer AUTHORED, each correctable in place (2026-08-06).
 *
 * The same pair of affordances the member page's Session-notes card carries,
 * and deliberately the same DECISIONS: both dispatch `edit_session_note` /
 * `delete_session_note` — the one write path onto `session_notes` (invariant 5)
 * — and both import `lib/session-note-edit.ts` rather than restating when a
 * draft may be saved or how a flagged delete must be worded. A second copy of
 * those rules is how the two pages would come to disagree about, say, whether
 * an unchanged body files an audit row.
 *
 * The shape differs because the surface does: the member page shows a stack of
 * cards for ONE member, this shows a table across MANY members, so the editor
 * takes over the Note cell rather than the whole row, keeping `When` and
 * `Member` legible as context while the body is corrected.
 *
 * `structured` is not editable here either — the flags bag is owned by
 * `remove_session_note_flag`, and a body edit able to carry it would let a typo
 * fix silently clobber an injury flag. The Details column stays read-only.
 */
function TrainerSessionNotes({
  notes,
  onChanged,
}: {
  readonly notes: ReadonlyArray<TrainerDetailSessionNote>;
  readonly onChanged: () => void;
}): JSX.Element {
  const [editingId, setEditingId] = useState<string | null>(null);
  /** The note a dispatch is in flight for — locks every control while set. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const save = async (noteId: string, body: string): Promise<void> => {
    if (busyId !== null) return;
    setBusyId(noteId);
    setErr(null);
    try {
      const r = await dispatchAction<
        { session_note_id: string; body: string },
        { member_id: string; updated_at: string }
      >("edit_session_note", { session_note_id: noteId, body });
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      setEditingId(null);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "edit failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (noteId: string, hasFlags: boolean): Promise<void> => {
    if (busyId !== null) return;
    // A HARD delete with no undo, so it asks once — and a flagged note gets the
    // sharper prompt, because deleting it also removes a safety flag from the
    // banner every trainer reads before a session.
    if (!window.confirm(sessionNoteDeleteConfirmMessage(hasFlags))) return;
    setBusyId(noteId);
    setErr(null);
    try {
      const r = await dispatchAction<
        { session_note_id: string },
        { deleted: boolean; member_id: string }
      >("delete_session_note", { session_note_id: noteId });
      if (!r.ok) {
        setErr(r.message);
        return;
      }
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section className="panel">
      <h2>Session notes ({notes.length})</h2>
      {err !== null ? <p className="cf-error">{err}</p> : null}
      {notes.length === 0 ? (
        <p className="empty">No notes authored.</p>
      ) : (
        <div className="table-scroll-x">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Member</th>
                <th>Note</th>
                <th>Details</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {notes.map((n) => {
                const hasFlags = noteHasFlags(n.structured);
                return (
                  <tr key={n.id}>
                    <td>{fmtDateTime(n.created_at)}</td>
                    <td>
                      <Link
                        to={`/members/${encodeURIComponent(n.member_id)}`}
                        className="data-member-link"
                      >
                        {n.member_name ?? n.member_id.slice(0, 8)}
                      </Link>
                    </td>
                    <td>
                      {editingId === n.id ? (
                        <SessionNoteEditor
                          initialBody={n.body}
                          busy={busyId === n.id}
                          onCancel={() => {
                            setEditingId(null);
                            setErr(null);
                          }}
                          onSave={(body) => void save(n.id, body)}
                        />
                      ) : (
                        n.body
                      )}
                    </td>
                    <td>
                      {Object.keys(n.structured).length === 0 ? (
                        "—"
                      ) : (
                        <CollapsibleJson value={n.structured} />
                      )}
                    </td>
                    {/* Hidden while this note is open for editing — the editor
                        carries its own Save / Cancel, and leaving these up would
                        offer two competing ways out of the same state. */}
                    <td>
                      {editingId === n.id ? null : (
                        <span className="session-note-actions">
                          <button
                            type="button"
                            className="flow-remove-btn"
                            title="Edit this note"
                            disabled={busyId !== null}
                            onClick={() => {
                              setErr(null);
                              setEditingId(n.id);
                            }}
                          >
                            ✎
                          </button>
                          <button
                            type="button"
                            className="flow-remove-btn"
                            title="Delete this note"
                            disabled={busyId !== null}
                            onClick={() => void remove(n.id, hasFlags)}
                          >
                            ×
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * Does this note carry at least one safety flag? Decides only which of the two
 * delete confirmations is shown, so it asks the narrowest possible question of
 * `structured` — the member page renders the flags themselves and needs their
 * contents; this page shows the raw bag in the Details column and does not.
 */
function noteHasFlags(structured: Record<string, unknown>): boolean {
  const flags = structured["flags"];
  return Array.isArray(flags) && flags.length > 0;
}


/**
 * Inline editor for a trainer's `email`, dispatched via the dedicated
 * `change_trainer_email` action.
 *
 * This is the ONLY field on this panel that is editable, and deliberately so:
 * `email` is the identity column (`UNIQUE (studio_id, email)`, and
 * `lookup_trainer`'s `identifier_kind: 'email'` key), which is exactly why it
 * gets its own action rather than riding `update_trainer`'s patch — and why,
 * until 2026-08-19, it could not be changed from anywhere at all. The rest of
 * the panel stays read-only; `/data/trainers` remains the surface for the
 * patchable profile fields.
 *
 * Errors surface verbatim from the chokepoint, which is the point of the
 * dedicated action: a collision reads `trainer_email_already_in_use` (409)
 * rather than a raw constraint violation dressed up as a server fault, and a
 * soft-deleted trainer reads `trainer_already_deleted` (their address was
 * cleared to keep it reusable on a re-add).
 */
function TrainerEmailEditor({
  trainerId,
  value,
  onSaved,
}: {
  readonly trainerId: string;
  readonly value: string | null;
  readonly onSaved: () => void;
}): JSX.Element {
  const [editing, setEditing] = useState<boolean>(false);
  const [draft, setDraft] = useState<string>(value ?? "");
  const dispatch = useDispatch<
    { readonly trainer_id: string; readonly new_email: string },
    { readonly changed: boolean }
  >("change_trainer_email");

  const begin = (): void => {
    setDraft(value ?? "");
    dispatch.reset();
    setEditing(true);
  };
  const cancel = (): void => {
    dispatch.reset();
    setEditing(false);
  };
  const save = async (): Promise<void> => {
    if (dispatch.loading || trainerId === "") return;
    const trimmed = draft.trim();
    // `new_email` is required by the schema — an empty draft is a no-op here
    // rather than a round-trip that can only come back `input_invalid`.
    if (trimmed === "") return;
    const r = await dispatch.run({
      trainer_id: trainerId,
      new_email: trimmed,
    });
    if (r.ok) {
      setEditing(false);
      onSaved();
    }
  };

  return (
    <ProfileEditRow
      editing={editing}
      onEdit={begin}
      display={value ?? "—"}
      editor={
        <input
          className="profile-field-input"
          type="email"
          value={draft}
          placeholder="name@example.com"
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void save();
            if (e.key === "Escape") cancel();
          }}
        />
      }
      onSave={() => void save()}
      onCancel={cancel}
      loading={dispatch.loading}
      error={dispatch.error?.message ?? null}
    />
  );
}
